import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, HeartPulse, RefreshCw, Pencil, Check, X, MapPin, ExternalLink, Store, ToggleLeft, ToggleRight, Phone, Trash2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { api } from '../../../services/api';
import { Breadcrumb } from './Breadcrumb';
import { StoreSummaryCards } from './StoreSummaryCards';
import { StoreProductTabs } from './StoreProductTabs';
import { AsIsProductsTable } from './AsIsProductsTable';
import { SaleProductsTable } from './SaleProductsTable';
import { PhysicalProductsTable } from './PhysicalProductsTable';
import { StoreHealthMetrics } from './StoreHealthMetrics';
import { HealthScoreCircle } from './HealthBadge';
import { SlugVerificationBadge, type VerificationStatus } from './SlugVerificationBadge';
import { AddressVerificationBadge, type AddressVerificationStatus, type AddressVerificationMethod } from './AddressVerificationBadge';

type TabType = 'asis' | 'last_chance' | 'family_offers' | 'limited_time' | 'physical' | 'health';

export function StoreDetailView() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { country, storeId } = useParams<{ country: string; storeId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get the page we came from (if navigated from list)
  const fromPage = (location.state as { fromPage?: number } | null)?.fromPage;

  // Build the back URL with page parameter if we came from a specific page
  const backUrl = fromPage && fromPage > 1
    ? `/admin/store-browser/${country}?page=${fromPage}`
    : `/admin/store-browser/${country}`;

  const tabParam = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(tabParam || 'asis');

  // Slug editing state
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [slugValue, setSlugValue] = useState('');
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const slugInputRef = useRef<HTMLInputElement>(null);

  // Name inline editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Address inline editing state
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressValue, setAddressValue] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // As-Is URL inline editing state
  const [isEditingAsIsUrl, setIsEditingAsIsUrl] = useState(false);
  const [asIsUrlValue, setAsIsUrlValue] = useState('');
  const [asIsUrlSaving, setAsIsUrlSaving] = useState(false);
  const [asIsUrlError, setAsIsUrlError] = useState<string | null>(null);
  const asIsUrlInputRef = useRef<HTMLInputElement>(null);


  // Update URL when tab changes
  useEffect(() => {
    if (activeTab !== tabParam) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, tabParam, setSearchParams]);

  // Scroll to top when entering the page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [storeId]);

  // Focus slug input when editing
  useEffect(() => {
    if (isEditingSlug && slugInputRef.current) {
      slugInputRef.current.focus();
      slugInputRef.current.select();
    }
  }, [isEditingSlug]);

  // Focus name input when editing
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Focus address input when editing
  useEffect(() => {
    if (isEditingAddress && addressInputRef.current) {
      addressInputRef.current.focus();
      addressInputRef.current.select();
    }
  }, [isEditingAddress]);
  // Focus as-is URL input when editing
  useEffect(() => {
    if (isEditingAsIsUrl && asIsUrlInputRef.current) {
      asIsUrlInputRef.current.focus();
      asIsUrlInputRef.current.select();
    }
  }, [isEditingAsIsUrl]);


  // Name editing handlers
  const handleNameEdit = () => {
    setNameValue(data?.store.name || '');
    setNameError(null);
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    const newName = nameValue.trim();

    if (!newName) {
      setNameError('Name is required');
      return;
    }

    // Skip if unchanged
    if (newName === data?.store.name) {
      setIsEditingName(false);
      return;
    }

    setNameSaving(true);
    setNameError(null);
    try {
      await api.updateStore(storeId!, { name: newName });
      setIsEditingName(false);
      queryClient.invalidateQueries({ queryKey: ['admin-store-browser-summary', storeId] });
      queryClient.invalidateQueries({ queryKey: ['admin-country-stores'] });
    } catch {
      setNameError('Failed to save');
    } finally {
      setNameSaving(false);
    }
  };

  const handleNameCancel = () => {
    setNameValue(data?.store.name || '');
    setNameError(null);
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleNameSave();
    else if (e.key === 'Escape') handleNameCancel();
  };

  // Address editing handlers
  const handleAddressEdit = () => {
    // Build current address string
    const parts = [data?.store.street_address, data?.store.city].filter(Boolean);
    setAddressValue(parts.join(', '));
    setAddressError(null);
    setIsEditingAddress(true);
  };

  const handleAddressSave = async () => {
    const rawAddress = addressValue.trim();

    if (!rawAddress) {
      setAddressError('Address is required');
      return;
    }

    setAddressSaving(true);
    setAddressError(null);
    try {
      await api.updateStore(storeId!, { raw_address: rawAddress });
      setIsEditingAddress(false);
      queryClient.invalidateQueries({ queryKey: ['admin-store-browser-summary', storeId] });
      queryClient.invalidateQueries({ queryKey: ['admin-country-stores'] });
    } catch {
      setAddressError('Failed to save');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleAddressCancel = () => {
    const parts = [data?.store.street_address, data?.store.city].filter(Boolean);
    setAddressValue(parts.join(', '));
    setAddressError(null);
    setIsEditingAddress(false);
  };

  const handleAddressKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAddressSave();
    else if (e.key === 'Escape') handleAddressCancel();
  };
  // As-Is URL editing handlers
  const handleAsIsUrlEdit = () => {
    // Decode URL for editing so special characters (like ñ) are readable
    const url = data?.store.as_is_url || '';
    try {
      setAsIsUrlValue(decodeURIComponent(url));
    } catch {
      setAsIsUrlValue(url);
    }
    setAsIsUrlError(null);
    setIsEditingAsIsUrl(true);
  };

  const handleAsIsUrlSave = async () => {
    const newUrl = asIsUrlValue.trim();

    if (newUrl === (data?.store.as_is_url || '')) {
      setIsEditingAsIsUrl(false);
      return;
    }

    if (newUrl && !newUrl.startsWith('http://') && !newUrl.startsWith('https://')) {
      setAsIsUrlError('URL must start with http:// or https://');
      return;
    }

    setAsIsUrlSaving(true);
    setAsIsUrlError(null);

    try {
      await api.updateStore(storeId!, { as_is_url: newUrl || '' });
      await queryClient.invalidateQueries({ queryKey: ['admin-store-browser-summary', storeId] });
      setIsEditingAsIsUrl(false);
    } catch {
      setAsIsUrlError('Failed to save URL');
    } finally {
      setAsIsUrlSaving(false);
    }
  };

  const handleAsIsUrlCancel = () => {
    setIsEditingAsIsUrl(false);
    setAsIsUrlError(null);
    setAsIsUrlValue('');
  };

  const handleAsIsUrlKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAsIsUrlSave();
    } else if (e.key === 'Escape') {
      handleAsIsUrlCancel();
    }
  };

  const handleSlugEdit = () => {
    setSlugValue(data?.store.slug || '');
    setSlugError(null);
    setIsEditingSlug(true);
  };

  const handleSlugSave = async () => {
    const newSlug = slugValue.trim().toLowerCase() || null;

    // Validate format - allow letters (including unicode), numbers, hyphens, plus signs
    if (newSlug && /\s/.test(newSlug)) {
      setSlugError('Slugs cannot contain spaces');
      return;
    }

    // Skip if unchanged
    if (newSlug === (data?.store.slug || null)) {
      setIsEditingSlug(false);
      return;
    }

    setSlugSaving(true);
    setSlugError(null);
    try {
      await api.updateStoreSlug(storeId!, newSlug);
      setIsEditingSlug(false);
      queryClient.invalidateQueries({ queryKey: ['admin-store-browser-summary', storeId] });
    } catch {
      setSlugError('Failed to save');
    } finally {
      setSlugSaving(false);
    }
  };

  const handleSlugCancel = () => {
    setSlugValue(data?.store.slug || '');
    setSlugError(null);
    setIsEditingSlug(false);
  };

  const handleSlugKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSlugSave();
    else if (e.key === 'Escape') handleSlugCancel();
  };

  // Fetch store summary
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-store-browser-summary', storeId],
    queryFn: () => api.getAdminStoreBrowserSummary(storeId!),
    enabled: !!storeId,
  });

  // Re-verify slug mutation
  const verifySlugMutation = useMutation({
    mutationFn: () => api.verifyStoreSlug(storeId!),
    onSuccess: () => {
      // Invalidate to refresh the data
      queryClient.invalidateQueries({ queryKey: ['admin-store-browser-summary', storeId] });
    },
  });

  // Toggle As-Is department support
  const toggleAsIsMutation = useMutation({
    mutationFn: (hasAsIs: boolean) => api.updateStoreAsIs(storeId!, hasAsIs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-store-browser-summary', storeId] });
    },
  });

  // Delete store mutation
  const deleteStoreMutation = useMutation({
    mutationFn: () => api.deleteStore(storeId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-country-stores'] });
      navigate(`/admin/store-browser/${country}`);
    },
  });

  // Address verification action mutation
  const addressVerificationMutation = useMutation({
    mutationFn: ({ action, notes }: { action: 'approve' | 'reject' | 'verify_manual' | 'unverify'; notes?: string }) =>
      api.updateAddressVerification(storeId!, action, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-store-browser-summary', storeId] });
      queryClient.invalidateQueries({ queryKey: ['admin-country-stores'] });
    },
  });

  // Note: Address fetching removed - now using crowdsourced address collection

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load store details</p>
        <button
          onClick={() => navigate(backUrl)}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Go back
        </button>
      </div>
    );
  }

  const countryName = data?.store.country || country || '';

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: countryName, href: `/admin/store-browser/${country}` },
          { label: data?.store.name || storeId || '' },
        ]}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(backUrl)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            {/* Editable Store Name */}
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  ref={nameInputRef}
                  type="text"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={handleNameKeyDown}
                  placeholder="Store Name"
                  disabled={nameSaving}
                  className={`text-2xl font-semibold px-2 py-1 border rounded ${
                    nameError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                />
                <button
                  onClick={handleNameSave}
                  disabled={nameSaving}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                  title="Save"
                >
                  <Check size={18} />
                </button>
                <button
                  onClick={handleNameCancel}
                  disabled={nameSaving}
                  className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                  title="Cancel"
                >
                  <X size={18} />
                </button>
                {nameError && <span className="text-sm text-red-500">{nameError}</span>}
              </div>
            ) : (
              <button
                type="button"
                className="text-2xl font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 px-2 py-1 -mx-2 rounded group inline-flex items-center gap-2 bg-transparent border-none text-left"
                onClick={handleNameEdit}
                title="Click to edit name"
              >
                {isLoading ? 'Loading...' : data?.store.name}
                <Pencil size={16} className="opacity-0 group-hover:opacity-50" />
              </button>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPin size={14} className="text-gray-400" />
              <span>
                {[data?.store.city, data?.store.state, data?.store.country].filter(Boolean).join(', ')}
              </span>
              <span className="text-gray-300">•</span>
              <span className="font-mono text-xs">{storeId}</span>
            </div>
            {/* Slug display with editing and verification */}
            <div className="flex items-center gap-2 mt-1">
              {isEditingSlug ? (
                <>
                  <span className="text-xs text-gray-500">slug:</span>
                  <input
                    ref={slugInputRef}
                    type="text"
                    value={slugValue}
                    onChange={(e) => setSlugValue(e.target.value)}
                    onKeyDown={handleSlugKeyDown}
                    placeholder="store-slug"
                    disabled={slugSaving}
                    className={`w-32 px-2 py-0.5 text-xs font-mono border rounded ${
                      slugError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                  />
                  <button
                    onClick={handleSlugSave}
                    disabled={slugSaving}
                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                    title="Save"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={handleSlugCancel}
                    disabled={slugSaving}
                    className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                    title="Cancel"
                  >
                    <X size={14} />
                  </button>
                  {slugError && <span className="text-xs text-red-500">{slugError}</span>}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-600 cursor-pointer hover:bg-gray-200 group flex items-center gap-1 border-none"
                    onClick={handleSlugEdit}
                    title="Click to edit slug"
                  >
                    slug: {data?.store.slug || <span className="italic text-gray-400">none</span>}
                    <Pencil size={10} className="opacity-0 group-hover:opacity-100" />
                  </button>
                  {data?.store.slug && (
                    <>
                      <SlugVerificationBadge
                        status={(data.store.slug_verification_status || 'never_checked') as VerificationStatus}
                        verifiedAt={data.store.slug_verified_at}
                        isLoading={verifySlugMutation.isPending}
                      />
                      <button
                        onClick={() => verifySlugMutation.mutate()}
                        disabled={verifySlugMutation.isPending}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                        title="Re-verify slug"
                      >
                        <RefreshCw size={14} className={verifySlugMutation.isPending ? 'animate-spin' : ''} />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {data && (
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
            >
              <Trash2 size={16} />
              Delete
            </button>
            <HealthScoreCircle score={data.health_score} size={56} strokeWidth={4} />
          </div>
        )}
      </div>

      {/* Location Card with Map */}
      {data && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex gap-4">
            {/* Map using OpenStreetMap */}
            {data.store.latitude && data.store.longitude ? (
              <div className="w-48 h-32 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                <iframe
                  title="Store Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${data.store.longitude - 0.01},${data.store.latitude - 0.01},${data.store.longitude + 0.01},${data.store.latitude + 0.01}&layer=mapnik&marker=${data.store.latitude},${data.store.longitude}`}
                />
              </div>
            ) : (
              <div className="w-48 h-32 rounded-lg border border-dashed border-gray-300 flex-shrink-0 flex items-center justify-center bg-gray-50">
                <span className="text-gray-400 text-sm">No location</span>
              </div>
            )}
            {/* Location Info with inline editing */}
            <div className="flex flex-col justify-center gap-2 flex-1">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">Store Address</p>
                  {!isEditingAddress && (
                    <button
                      onClick={handleAddressEdit}
                      className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="Edit address"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                </div>
                {isEditingAddress ? (
                  <div className="mt-1">
                    <div className="flex items-center gap-2">
                      <input
                        ref={addressInputRef}
                        type="text"
                        value={addressValue}
                        onChange={(e) => setAddressValue(e.target.value)}
                        onKeyDown={handleAddressKeyDown}
                        placeholder="Paste address: Street, City"
                        disabled={addressSaving}
                        className={`flex-1 px-2 py-1.5 text-sm border rounded ${
                          addressError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                        } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                      />
                      <button
                        onClick={handleAddressSave}
                        disabled={addressSaving}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        title="Save"
                      >
                        {addressSaving ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <Check size={14} />
                        )}
                      </button>
                      <button
                        onClick={handleAddressCancel}
                        disabled={addressSaving}
                        className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {addressError && <p className="text-xs text-red-500 mt-1">{addressError}</p>}
                    <p className="text-xs text-gray-400 mt-1">
                      Paste from IKEA: "Dybbølsbro 4, København V"
                    </p>
                  </div>
                ) : data.store.street_address ? (
                  <button
                    type="button"
                    className="text-sm text-gray-600 cursor-pointer hover:bg-gray-50 px-1 py-0.5 -mx-1 rounded group bg-transparent border-none text-left"
                    onClick={handleAddressEdit}
                    title="Click to edit"
                  >
                    <p className="flex items-center gap-1">
                      {data.store.street_address}
                      <Pencil size={10} className="opacity-0 group-hover:opacity-50" />
                    </p>
                    <p>
                      {[data.store.city, data.store.state, data.store.postal_code].filter(Boolean).join(', ')}
                    </p>
                    <p>{data.store.country}</p>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="text-sm text-gray-400 italic cursor-pointer hover:bg-gray-50 px-1 py-0.5 -mx-1 rounded bg-transparent border-none text-left"
                    onClick={handleAddressEdit}
                    title="Click to add address"
                  >
                    Click to add address
                  </button>
                )}
              </div>
              {data.store.phone_number && (
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Phone size={14} className="text-gray-400" />
                  {data.store.phone_number}
                </div>
              )}
              <div className="flex items-center gap-3 mt-1">
                {data.store.latitude && data.store.longitude && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${data.store.latitude},${data.store.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <ExternalLink size={14} />
                    Google Maps
                  </a>
                )}
                {/* Address Verification Status Badge */}
                <AddressVerificationBadge
                  status={data.store.address_verification_status as AddressVerificationStatus}
                  method={data.store.address_verification_method as AddressVerificationMethod}
                  verifiedAt={data.store.address_verified_at}
                  submitterCount={data.store.address_submission_count}
                />
                {/* Admin Actions */}
                {data.store.address_verification_status === 'verified' && (
                  <button
                    onClick={() => addressVerificationMutation.mutate({ action: 'unverify' })}
                    disabled={addressVerificationMutation.isPending}
                    className="text-xs text-gray-500 hover:text-red-600 hover:underline disabled:opacity-50"
                    title="Remove verification"
                  >
                    Unverify
                  </button>
                )}
                {(!data.store.address_verification_status ||
                  ['unverified', 'pending', 'rejected', 'legacy'].includes(data.store.address_verification_status)) && (
                  <button
                    onClick={() => addressVerificationMutation.mutate({ action: 'verify_manual' })}
                    disabled={addressVerificationMutation.isPending}
                    className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline disabled:opacity-50"
                    title="Manually verify this address"
                  >
                    <ShieldCheck size={12} />
                    Verify Manually
                  </button>
                )}
              </div>
              {/* Pending Approval Banner */}
              {(data.store.address_verification_status === 'pending_approval' || data.store.address_verification_status === 'pending_review') && data.store.proposed_address && (
                <div className={`mt-3 p-3 rounded-lg border ${
                  data.store.address_verification_status === 'pending_approval'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-orange-50 border-orange-200'
                }`}>
                  <div className="flex items-start gap-2">
                    <ShieldAlert size={16} className={data.store.address_verification_status === 'pending_approval' ? 'text-amber-600 mt-0.5' : 'text-orange-600 mt-0.5'} />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${data.store.address_verification_status === 'pending_approval' ? 'text-amber-800' : 'text-orange-800'}`}>
                        {data.store.address_verification_status === 'pending_approval' ? 'Address Change Pending Approval' : 'Address Needs Review'}
                      </p>
                      <p className={`text-xs ${data.store.address_verification_status === 'pending_approval' ? 'text-amber-600' : 'text-orange-600'} mt-0.5`}>
                        {data.store.proposed_address.unique_submitter_count} users confirmed this address
                        {data.store.proposed_address.geocoding_distance_km !== null && (
                          <span className="ml-1">
                            ({data.store.proposed_address.geocoding_distance_km.toFixed(1)} km from store location)
                          </span>
                        )}
                        {data.store.proposed_address.geocoding_status === 'failed_geocoding' && (
                          <span className="ml-1 text-orange-700">(geocoding failed)</span>
                        )}
                        {data.store.proposed_address.geocoding_status === 'failed_distance' && (
                          <span className="ml-1 text-orange-700">(too far from store)</span>
                        )}
                      </p>
                      <div className="mt-2 p-2 bg-white/50 rounded text-sm">
                        <p className="font-medium text-gray-900">Proposed Address:</p>
                        <p className="text-gray-700">{data.store.proposed_address.street_address}</p>
                        <p className="text-gray-600">
                          {[data.store.proposed_address.city, data.store.proposed_address.postal_code].filter(Boolean).join(', ')}
                        </p>
                        {data.store.proposed_address.phone_number && (
                          <p className="text-gray-600 text-xs mt-1">Phone: {data.store.proposed_address.phone_number}</p>
                        )}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => addressVerificationMutation.mutate({ action: 'approve' })}
                          disabled={addressVerificationMutation.isPending}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check size={14} />
                          Approve
                        </button>
                        <button
                          onClick={() => addressVerificationMutation.mutate({ action: 'reject' })}
                          disabled={addressVerificationMutation.isPending}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                          <X size={14} />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {data.store.address_verified_at && (
                <p className="text-xs text-gray-400 mt-1">
                  Verified: {new Date(data.store.address_verified_at).toLocaleDateString()}
                  {data.store.address_verification_method && ` (${data.store.address_verification_method})`}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Store Settings Card */}
      {data && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-4">
            <Store size={18} className="text-gray-600" />
            <h3 className="font-medium text-gray-900">Store Settings</h3>
          </div>
          <div className="space-y-4">
            {/* As-Is Department Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">As-Is Department</p>
                <p className="text-xs text-gray-500">
                  Does this store have an As-Is/Bargain Corner department?
                </p>
                {data.store.last_as_is_check && (
                  <p className="text-xs text-gray-400 mt-1">
                    Last checked: {new Date(data.store.last_as_is_check).toLocaleDateString()}
                    {data.store.as_is_detection_method && (
                      <span className="ml-1">({data.store.as_is_detection_method})</span>
                    )}
                  </p>
                )}
              </div>
              <button
                onClick={() => toggleAsIsMutation.mutate(!(data.store.has_as_is ?? true))}
                disabled={toggleAsIsMutation.isPending}
                className={`relative flex items-center transition-colors ${
                  toggleAsIsMutation.isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
                title={data.store.has_as_is !== false ? 'Click to mark as No As-Is' : 'Click to mark as Has As-Is'}
              >
                {data.store.has_as_is !== false ? (
                  <ToggleRight size={36} className="text-green-500" />
                ) : (
                  <ToggleLeft size={36} className="text-gray-400" />
                )}
              </button>
            </div>
            {/* As-Is URL */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900">As-Is Page URL</p>
                {!isEditingAsIsUrl && (
                  <button
                    onClick={handleAsIsUrlEdit}
                    className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Edit URL"
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </div>
              {isEditingAsIsUrl ? (
                <div className="mt-1">
                  <div className="flex items-center gap-2">
                    <input
                      ref={asIsUrlInputRef}
                      type="text"
                      value={asIsUrlValue}
                      onChange={(e) => setAsIsUrlValue(e.target.value)}
                      onKeyDown={handleAsIsUrlKeyDown}
                      placeholder="https://www.ikea.com/..."
                      disabled={asIsUrlSaving}
                      className={`flex-1 px-2 py-1.5 text-sm border rounded ${
                        asIsUrlError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      } focus:outline-none focus:ring-1 focus:ring-primary-500`}
                    />
                    <button
                      onClick={handleAsIsUrlSave}
                      disabled={asIsUrlSaving}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                      title="Save"
                    >
                      {asIsUrlSaving ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <Check size={14} />
                      )}
                    </button>
                    <button
                      onClick={handleAsIsUrlCancel}
                      disabled={asIsUrlSaving}
                      className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {asIsUrlError && <p className="text-xs text-red-500 mt-1">{asIsUrlError}</p>}
                </div>
              ) : data.store.as_is_url ? (
                <button
                  type="button"
                  className="group cursor-pointer hover:bg-gray-50 px-1 py-0.5 -mx-1 rounded mt-1 bg-transparent border-none text-left"
                  onClick={handleAsIsUrlEdit}
                  title="Click to edit"
                >
                  <a
                    href={data.store.as_is_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm text-primary-600 hover:text-primary-700 hover:underline break-all inline-flex items-center gap-1"
                  >
                    {(() => { try { return decodeURIComponent(data.store.as_is_url); } catch { return data.store.as_is_url; } })()}
                    <ExternalLink size={12} />
                  </a>
                  <Pencil size={10} className="ml-1 opacity-0 group-hover:opacity-50 inline" />
                </button>
              ) : (
                <button
                  type="button"
                  className="text-sm text-gray-400 italic cursor-pointer hover:bg-gray-50 px-1 py-0.5 -mx-1 rounded mt-1 bg-transparent border-none text-left"
                  onClick={handleAsIsUrlEdit}
                  title="Click to add URL"
                >
                  Click to add As-Is URL
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-32 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      ) : data ? (
        <StoreSummaryCards
          asis={data.asis}
          lastChance={data.last_chance}
          familyOffers={data.family_offers}
          limitedTime={data.limited_time}
          physical={data.physical}
          storeId={storeId!}
          country={data.store.country}
          slug={data.store.slug}
        />
      ) : null}

      {/* Tabs + Content */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-4">
          <div className="flex gap-4">
            <StoreProductTabs
              activeTab={activeTab === 'health' ? 'asis' : activeTab}
              onTabChange={setActiveTab}
              counts={{
                asis: data?.asis.total_products || 0,
                last_chance: data?.last_chance?.total_products || 0,
                family_offers: data?.family_offers?.total_products || 0,
                limited_time: data?.limited_time?.total_products || 0,
                physical: data?.physical.total_products || 0,
              }}
            />
            {/* Health Tab */}
            <button
              onClick={() => setActiveTab('health')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'health'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <HeartPulse size={18} />
              <span className="font-medium">Health Report</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'asis' && storeId && <AsIsProductsTable storeId={storeId} country={data?.store.country || ""} />}
          {activeTab === 'last_chance' && storeId && <SaleProductsTable storeId={storeId} country={data?.store.country || ""} saleType="last_chance" />}
          {activeTab === 'family_offers' && storeId && <SaleProductsTable storeId={storeId} country={data?.store.country || ""} saleType="family_offers" />}
          {activeTab === 'limited_time' && storeId && <SaleProductsTable storeId={storeId} country={data?.store.country || ""} saleType="limited_time" />}
          {activeTab === 'physical' && storeId && <PhysicalProductsTable storeId={storeId} />}
          {activeTab === 'health' && storeId && <StoreHealthMetrics storeId={storeId} />}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/30"
            onClick={() => setShowDeleteConfirm(false)}
            onKeyDown={(e) => e.key === 'Escape' && setShowDeleteConfirm(false)}
            role="button"
            tabIndex={0}
            aria-label="Close modal"
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete Store?</h2>
              <p className="text-sm text-gray-600 mb-4">
                This will deactivate <strong>{data?.store.name}</strong> and remove it from all seed assignments.
                This action can be undone by reactivating the store.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteStoreMutation.mutate()}
                  disabled={deleteStoreMutation.isPending}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteStoreMutation.isPending ? 'Deleting...' : 'Delete Store'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
